import { getElementOrParentByClassName } from '../helpers/utils';
import '../../styles/components/_dialog.scss';
import Scroll from '../modules/Scroll';
import { isOverlayShown } from '../modules/Overlay';
import { preventAnimation } from '../services/accessibility.service';
const DIALOG_ANIMATION_MS = 300;
const CONTENT_READY_DELAY_MS = 75;
class DialogWrapper extends HTMLElement {
    constructor() {
        super();
        this.onclose = this.onClose.bind(this);
        this.closeButtonClick = this.onCloseButtonClick.bind(this);
    }
    dialog;
    onclose;
    closeButtonClick;
    contentObserver;
    contentReadyTimer;
    isClosing = false;
    connectedCallback() {
        console.debug("dialog-wrapper connected");
        const template = document.getElementById('utk-main-dialog-template');
        if (!template) {
            console.error(`dialog template (${template}) not defined; unable to generate dialog`);
        }
        else {
            this.dialog = template.content.firstElementChild.cloneNode(true);
            this.append(this.dialog);
            this.dialog.addEventListener("close", this.onclose);
            this.dialog.addEventListener("click", this.closeButtonClick);
            this.setupContentTransition();
        }
    }
    disconnectedCallback() {
        console.debug("dialog-wrapper disconnected");
        if (this.dialog) {
            this.dialog.removeEventListener("close", this.onclose);
            this.dialog.removeEventListener("click", this.closeButtonClick);
        }
        this.contentObserver?.disconnect();
        window.clearTimeout(this.contentReadyTimer);
    }
    onClose() {
        this.remove();
    }
    onCloseButtonClick(e) {
        const target = e.target;
        if (getElementOrParentByClassName(target, 'main-dialog__close-button')) {
            e.stopPropagation(); // prevents an inner dialog from closing an outer dialog
            this.close();
        }
    }
    setContent(...content) {
        const contentEl = this.getContentEl();
        contentEl.classList.remove('is-content-ready');
        contentEl.append(...content);
    }
    showDialog() {
        this.openDialog(false);
    }
    showModal() {
        this.openDialog(true);
    }
    close() {
        if (this.isClosing)
            return;
        if (preventAnimation()) {
            this.finishClose();
            return;
        }
        this.isClosing = true;
        this.dialog.classList.add('is-closing');
        const onTransitionEnd = (event) => {
            if (event.target !== this.dialog)
                return;
            if (event.propertyName !== 'opacity' && event.propertyName !== 'transform')
                return;
            this.dialog.removeEventListener('transitionend', onTransitionEnd);
            window.clearTimeout(fallbackTimer);
            this.finishClose();
        };
        this.dialog.addEventListener('transitionend', onTransitionEnd);
        const fallbackTimer = window.setTimeout(() => {
            this.dialog.removeEventListener('transitionend', onTransitionEnd);
            if (this.isClosing) {
                this.finishClose();
            }
        }, DIALOG_ANIMATION_MS + 50);
    }
    getDialogEl() {
        return this.dialog;
    }
    isOpen() {
        return this.dialog?.open;
    }
    openDialog(modal) {
        if (modal) {
            Scroll.lock();
            this.dialog.showModal();
        }
        else {
            this.dialog.show();
        }
        this.markContentReadyWhenVisible();
    }
    finishClose() {
        this.isClosing = false;
        this.dialog.classList.remove('is-closing');
        this.dialog.close();
        if (!document.querySelector("dialog[open]") && !isOverlayShown()) {
            Scroll.unlock();
        }
    }
    getContentEl() {
        return this.dialog.querySelector('.main-dialog__content');
    }
    setupContentTransition() {
        const contentEl = this.getContentEl();
        const onMutation = () => {
            if (!contentEl.classList.contains('is-content-ready') && contentEl.childElementCount > 0 && this.dialog.open) {
                this.markContentReadyWhenVisible();
            }
        };
        this.contentObserver = new MutationObserver(onMutation);
        this.contentObserver.observe(contentEl, { childList: true });
    }
    markContentReadyWhenVisible() {
        const contentEl = this.getContentEl();
        if (contentEl.childElementCount === 0)
            return;
        window.clearTimeout(this.contentReadyTimer);
        const delay = preventAnimation() ? 0 : CONTENT_READY_DELAY_MS;
        this.contentReadyTimer = window.setTimeout(() => {
            if (contentEl.childElementCount > 0) {
                contentEl.classList.add('is-content-ready');
            }
        }, delay);
    }
}
if (!window.customElements.get("dialog-wrapper")) {
    customElements.define("dialog-wrapper", DialogWrapper);
}
